import { Provider } from 'react-redux';
import { Route, Router, Switch } from 'react-router-dom';
import 'modern-normalize';
import '@redis-ui/styles/normalized-styles.css';
import './app.css';
import { I18nextProvider } from 'react-i18next';
import { CommonStyles } from '@redis-ui/styles';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { analytics } from '@redislabsdev/cloud-ui-analytics';
import { QueryClientProvider } from '@tanstack/react-query';
import { routes } from 'utils/constants/routes';
import LogOut from 'screens/LogOut';
import CaptchaFrame from 'components/CaptchaFrame/CaptchaFrame';
import { TooltipProvider } from '@redis-ui/components';
import FeatureFlagsProvider from 'components/FeatureFlagsProvider/FeatureFlagsProvider';
import { BotProtection } from 'components/BotProtection/BotProtection';
import Maintenance from 'screens/Maintenance/Maintenance';
import { ThemesProvider } from 'themes';

import { Config } from 'config';
import { AppToaster } from './components/Toast/AppToaster';
import i18n from './locale/i18n';
import store, { sagaMiddleware } from './store';
import rootSaga from './store/saga.root';
import history from './hashHistory';
import { ResponsiveRem } from './utils/style/responsive.style';
import { queryClient } from './utils/initializeQueryClient';
import AuthBroadcast from './services/broadcastChannels/AuthBroadcast/AuthBroadcast';
import InitializeOkta from './screens/AuthenticationWrappers/InitializeOkta';
import { CheckPendingTestConnection } from './screens/AuthenticationWrappers/CheckPendingTestConnection';
import SmAuthWrapper from './screens/AuthenticationWrappers/SmAuthWrapper';
import Routes from './screens/Routes';
import { LoadSystemParameters } from './screens/AuthenticationWrappers/LoadSystemParameters';
import { RedirectToSignUp } from './screens/RedirectToSignUp';
import { DialogIndicatorContextProvider } from './utils/helpers/dialogIndicatorContext';
import ParseTokens from './screens/AuthenticationWrappers/ParseTokens';
import { LoadCurrentUserAndPermissions } from './screens/AuthenticationWrappers/LoadCurrentUserAndPermissions';
import ValidateCookiesEnabled from './components/ValidateCookiesEnabled/ValidateCookiesEnabled';
import initMfesApiServices from './services/initMfesApiServices/initMfesApiServices';
import { AccountIdAwareServiceInitializer } from './components/RdiMFE/AccountIdAwareServiceInitializer';

sagaMiddleware?.run(rootSaga);
initMfesApiServices();

if (Config.ANALYTICS_KEYS.segment && Config.ANALYTICS_KEYS.amplitude) {
  analytics.loadWithGuidesAndSurveys({
    segmentKey: Config.ANALYTICS_KEYS.segment,
    amplitudeKey: Config.ANALYTICS_KEYS.amplitude,
    cdnUrl: Config.SEGMENT_CDN_URL
  });
}

function App() {
  AuthBroadcast.useChannel();

  return (
    <ThemesProvider>
      <CommonStyles />
      <TooltipProvider>
        <CaptchaFrame />
        <I18nextProvider i18n={i18n}>
          <ValidateCookiesEnabled>
            <DialogIndicatorContextProvider>
              <Provider store={store}>
                <QueryClientProvider client={queryClient}>
                  <BotProtection />
                  <ResponsiveRem />
                  <Router history={history}>
                    <Switch>
                      <Route exact path={routes.logout} component={LogOut} />
                      <Route exact path={routes.maintenance} component={Maintenance} />
                      <LoadSystemParameters>
                        <RedirectToSignUp>
                          <InitializeOkta>
                            <ParseTokens>
                              <LoadCurrentUserAndPermissions>
                                <AccountIdAwareServiceInitializer />
                                <CheckPendingTestConnection>
                                  <SmAuthWrapper>
                                    <FeatureFlagsProvider>
                                      <Routes />
                                    </FeatureFlagsProvider>
                                  </SmAuthWrapper>
                                </CheckPendingTestConnection>
                              </LoadCurrentUserAndPermissions>
                            </ParseTokens>
                          </InitializeOkta>
                        </RedirectToSignUp>
                      </LoadSystemParameters>
                    </Switch>
                  </Router>
                  <ReactQueryDevtools position="bottom-right" />
                </QueryClientProvider>
              </Provider>
            </DialogIndicatorContextProvider>
            <AppToaster />
          </ValidateCookiesEnabled>
        </I18nextProvider>
      </TooltipProvider>
    </ThemesProvider>
  );
}

export default App;
